import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import RolesModal from '../../Catalogos/Roles/RolesModal';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const EMPTY = {
  idUsuario: 0,
  idRol: 0,
};

const endpointUsuarios = ENDPOINTS.usuarios;
const endpointRoles = ENDPOINTS.roles;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    // Si queremos cerrar este modal al mostrar el alert como pidió el usuario:
    // onClose(); 
    // Pero si es un error interno del modal, tal vez solo queremos mostrar el alert encima?
    // El usuario dijo "si hay un modal abierto de otra indole y debe aparecer el alert() se cierra primero el modal"
    setAlertConfig({ open: true, message, variant, title });
  };

  const [usuariosOptions, setUsuariosOptions] = useState([]);
  const [rolesOptions, setRolesOptions] = useState([]);

  const [rolesOpen, setRolesOpen] = useState(false);
  const [savingRol, setSavingRol] = useState(false);

  // Cargar catálogos de usuarios y roles
  const loadCatalogs = async () => {
    try {
      const [resUsuarios, resRoles] = await Promise.all([
        axios.get(endpointUsuarios),
        axios.get(endpointRoles),
      ]);

      const listaUsuarios = (resUsuarios.data || []).map((x) => Models.usuarios(x));
      const listaRoles = (resRoles.data || []).map((x) => Models.roles(x));

      setUsuariosOptions(listaUsuarios);
      setRolesOptions(listaRoles);
    } catch (e) {
      console.error('Error cargando catálogos de usuarios/roles', e);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadCatalogs();
  }, [open]);

  // setear datos en edición o limpiar en modo new
  useEffect(() => {
    if (mode === 'edit' && data) {
      setForm({
        idUsuario: data.idUsuario ?? 0,
        idRol: data.idRol ?? 0,
      });
    } else {
      setForm(EMPTY);
    }
  }, [mode, data]);

  if (!open) return null;

  // 👇 Se llama cuando guardas en el RolesModal
  const handleSubmitNuevoRol = async (rolForm) => {
    try {
      setSavingRol(true);

      // 🔥 Mapear al contrato correcto del API
      const payload = {
        nombreRol: rolForm.nombreRol ?? rolForm.nombre ?? '',
        descripcion: rolForm.descripcion ?? '',
        esSistema: !!(rolForm.esSistema ?? false),
      };

      const res = await axios.post(endpointRoles, payload);
      const created = res?.data || {};

      // refrescar roles
      await loadCatalogs();

      // Extraer el ID de forma robusta
      let newId = 0;
      if (typeof created === 'number') {
        newId = created;
      } else if (typeof created === 'object' && created !== null) {
        newId = created.idRol ?? created.IdRol ?? created.id ?? created.Id ?? 0;
      }

      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el select se haya actualizado con las nuevas opciones
        setTimeout(() => {
          setForm((f) => ({ ...f, idRol: newId }));
        }, 100);
      }

      setRolesOpen(false);
    } catch (e) {
      console.error(e);
      triggerAlert('No se pudo crear el rol.');
    } finally {
      setSavingRol(false);
    }
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva asignación' : 'Editar asignación'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Usuario como select (nombreUsuario visible, idUsuario como value) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Usuario:</label>
        <br /><br />
        <select
          className="infoField"
          value={form.idUsuario ?? 0}
          onChange={(e) =>
            setForm({
              ...form,
              idUsuario: e.target.value ? Number(e.target.value) : 0,
            })
          }
        >
          <option value={0}>— Selecciona usuario —</option>
          {usuariosOptions.map((u) => (
            <option key={u.idUsuario} value={u.idUsuario}>
              {u.nombreUsuario}
            </option>
          ))}
        </select>
      </div>

      {/* Rol como select + botón para crear nuevo rol */}
      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Rol:
          <button
            type="button"
            className="btn-action"
            onClick={() => setRolesOpen(true)}
            title="Crear nuevo rol"
            style={{ padding: '2px 8px', lineHeight: 1 }}
          >
            ＋ Rol
          </button>
        </label>
        <br /><br />
        <select
          className="infoField"
          value={form.idRol ?? 0}
          onChange={(e) =>
            setForm({
              ...form,
              idRol: e.target.value ? Number(e.target.value) : 0,
            })
          }
        >
          <option value={0}>— Selecciona rol —</option>
          {rolesOptions.map((r) => (
            <option key={r.idRol} value={r.idRol}>
              {r.nombre}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="btn-cancel" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => onSubmit(form, mode)}
        disabled={!Number(form.idUsuario) || !Number(form.idRol)}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}

        {/* Modal de Roles embebido */}
        <RolesModal
          open={rolesOpen}
          mode="new"
          onClose={() => setRolesOpen(false)}
          onSubmit={handleSubmitNuevoRol}
        />
        {savingRol && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
        )}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>

      {/* Modal de Roles */}
      <RolesModal
        open={rolesOpen}
        mode="new"
        onClose={() => setRolesOpen(false)}
        onSubmit={handleSubmitNuevoRol}
      />
      {savingRol && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
      )}

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
